import Flow from '@/flows/flow';
import { buildAccountBaseService } from '@/common/service/base-paid';
import PageLoader from '@/common/service/flow/page-loader';
import { computedPbxBasePlans, computedPbxCallingPlans } from '@/common/service/computers/plans/pbx';
import { loadSelectByPersist } from '@/common/service/plan-data-mapper';
import { planTypes, VIEWS } from '@/common/constants';
import { addToCart } from '@/common/service/flow/utils';
import { get, has } from 'lodash';
import store from '@/store';
import { updatePlansCheckout, UpdateTypes } from '@/common/service/checkout-utils';
import { getPlansMap } from '@/common/product';

/**@start buildShoppingCart */

function loadDefaultSelectByQuery(sourcePlan, boughtPlans) {
  return (planList) => {
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    if (hasSelected) return planList;

    const _pbxCallingBillingCycle = sourcePlan.billing_cycle;
    const _pbxCallingLicense = sourcePlan.cartInfo.selected_number;
    const _pbxCallingRegion = sourcePlan.region;
    const _pbxBoughtBasePlan = computedPbxBasePlans(boughtPlans)[0];

    const _targetPlan = planList.find(item => (
      item.type === planTypes.ZONE_PRO_PLUS &&
      item.region === _pbxCallingRegion &&
      item.billing_cycle === _pbxCallingBillingCycle
    ));

    _targetPlan && addToCart(_targetPlan, _pbxCallingLicense);

    // add pbx base plan to shopping cart
    if (_pbxBoughtBasePlan) {
      const _pbxBasePlan = planList.find(item => item.plan_code === _pbxBoughtBasePlan.plan_code);
      _pbxBasePlan && addToCart(_pbxBasePlan, 1);
    }

    return planList;
  }
}

async function buildShoppingCart(state) {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();

  // get calling plan
  const [_firstCallingPlan] = computedPbxCallingPlans(boughtPlans);
  if (!_firstCallingPlan) {
    throw new Error('no valid pbx calling plan');
  }

  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQuery(_firstCallingPlan, boughtPlans));
  const planList = await loader.dispatch();
  
  return {
    sourcePlan: _firstCallingPlan,
    allPlanMap,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  }
}
/**@end buildShoppingCart */

/**@start beforeSubmit */

function checkLicense(getters) {
  const sourcePlanLicense = get(getters.sourcePlan, 'cartInfo.selected_number', 1);
  const selectedPlanLicese = get(getters.getSelectedPlans, '0.cartInfo.selected_number', 1);
  if (selectedPlanLicese < sourcePlanLicense) {
    return Promise.reject(`Can not decrease license: souceLicense = ${sourcePlanLicense}, currentLicense = ${selectedPlanLicese}`);
  }
  return Promise.resolve();
}

async function beforeSubmit() {
  await checkLicense(store.getters);
}
/**@end beforeSubmit */

/**@start submit */

async function submit(submitParams) {
  const getters = store.getters || {};

  const { sourcePlan, orderDataPayload, selectedPlanListPayLoad } = getters;
  const oldPlanCode = sourcePlan?.plan_code;
  const boughtPlanMap = getPlansMap(getters.boughtPlans);

  // Need confirm with back-end teammates.
  const actionType = UpdateTypes.PRO_TO_PTO_PLUS;
  const planList = selectedPlanListPayLoad
  .map(payload => ({ ...payload, oldPlanCode }))
  .filter(payload => !has(boughtPlanMap, payload.planCode));

  const couponData = orderDataPayload?.coupon;
  const extensionUpdateData = {};
  return updatePlansCheckout(submitParams, planList, actionType, couponData, extensionUpdateData);
}
/**@end submit */

export default class extends Flow {
  view() {
    return VIEWS.COMPOUND;
  }
  init() {
    return buildShoppingCart(...arguments);
  }
  beforeSubmit($store) {
    return beforeSubmit($store);
  }
  async submit(submitParams) {
    return submit(submitParams);
  }
}
